import { STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { SUDOKU_SIZES, type SudokuSize } from './sudoku-solve.js'
import { TECHNIQUES, techniquesUpTo, type Technique } from './sudoku-techniques.js'

/**
 * What she is getting better at, in Stripey's Sudoku.
 *
 * **A skill is a grid size.** Not a technique, which is what Rocky's Riddles
 * records — and the difference is worth understanding rather than copying. A
 * riddle is one of six in a round and each is made of one hardest clue kind, so
 * crediting the kind is crediting the thing she actually did. A sudoku is one
 * grid for ten minutes, and what she meets in it is every technique the grid
 * happens to need, in an order nobody chose. Recording it under the hardest one
 * would move a level for a step she took once, five minutes in.
 *
 * The size is the honest unit: it is what she chose, it is what she spent the
 * round on, and it is the thing that gets easier with practice.
 */
export interface SudokuSkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  readonly size: SudokuSize
}

export const SUDOKU_SKILLS: readonly SudokuSkill[] = [
  { key: 'sudoku.4x4', label: 'Four-by-four grids', size: 4 },
  { key: 'sudoku.6x6', label: 'Six-by-six grids', size: 6 },
  { key: 'sudoku.9x9', label: 'Nine-by-nine grids', size: 9 },
]

export function sudokuSkillOf(size: SudokuSize): SudokuSkill {
  const skill = SUDOKU_SKILLS.find((candidate) => candidate.size === size)
  // The list above is exhaustive over SudokuSize by construction and a test
  // holds that, so reaching here is a missing entry rather than a bad input.
  if (!skill) throw new Error(`No skill for a ${size}×${size} grid`)

  return skill
}

/** Where she stands at this size, or the starting level if she has never played it. */
export function sudokuLevelFor(levels: SkillLevels, size: SudokuSize): number {
  return levels[sudokuSkillOf(size).key] ?? STARTING_LEVEL
}

/**
 * How hard a puzzle at this size and level is.
 *
 * Note what is **not** here: a number of givens. Digging stops when the puzzle
 * stops being solvable by the techniques allowed, so how many givens are left
 * is an *outcome* of the difficulty rather than a setting of it. That is the
 * ticket's rule — two puzzles with the same count of givens differ wildly, and
 * tuning on the count makes puzzles emptier rather than harder.
 *
 * `atLeast` is the one number that is a setting, and it is a floor rather than
 * a target: it stops a 4×4 at level 0 being dug down to four givens simply
 * because it still technically scans out.
 */
export interface SudokuShapeForLevel {
  readonly size: SudokuSize
  /** The hardest technique a puzzle at this level may require. */
  readonly hardest: Technique
  /** The techniques a solver may use, which is every one up to `hardest`. */
  readonly allowed: readonly Technique[]
  /** Never dig below this many givens, whatever the solver says. */
  readonly atLeast: number
}

/**
 * The technique ladder, which is the same at every size.
 *
 * Plain scanning for a long while — it is the whole of sudoku for a beginner
 * and there is no hurry — then having to ask "where *can* this one go?", and
 * only at the top the step of ruling two squares out for somebody else.
 */
function hardestAt(level: number): Technique {
  if (level <= 3) return 'naked-single'
  if (level <= 7) return 'hidden-single'

  return 'naked-pair'
}

/** How much of a beginner's grid is filled in for her, and of an expert's. */
const MOST_GIVEN = 0.62
const FEWEST_GIVEN = 0.28

/**
 * The fewest givens worth leaving, as a share of the grid.
 *
 * Eases off smoothly as she improves — nearly two thirds of a beginner's grid
 * is already filled in, a bit over a quarter of an expert's.
 *
 * **Smoothly, and not in bands.** It was written in four bands first, and the
 * bands did not line up with the three technique tiers: levels five, six and
 * seven came out with the same floor *and* the same techniques, so they dealt
 * indistinguishable puzzles and three levels of her ladder did nothing at all.
 * A sweep caught it by insisting each level leaves fewer givens than the one
 * below, which is now a guarantee rather than an observation.
 *
 * This is a floor and not a target: at the top of the ladder the solver stops
 * the dig above it, and that is the right way round. It matters most at the
 * bottom, where a 4×4 will happily scan out from four givens and be no fun.
 */
function floorFor(size: SudokuSize, level: number): number {
  const cells = size * size
  const share = MOST_GIVEN + (FEWEST_GIVEN - MOST_GIVEN) * (level / 10)

  return Math.max(size, Math.round(cells * share))
}

export function sudokuShapeFor(size: SudokuSize, level: number): SudokuShapeForLevel {
  /*
   * A level that is not a number at all falls to the bottom of the ladder.
   *
   * `Math.max(0, Math.min(10, ...))` clamps -5 and 99 to the ends, which is
   * what the comment on this function promised — but every comparison against
   * `NaN` is false, so it slipped through both and came out with the *hardest*
   * technique and a floor of `NaN`: the opposite of the intended fallback, and
   * the one case where a corrupt level would hand a child the hardest puzzle
   * this game can deal.
   *
   * `NaN` only, and not "not finite": infinity clamps perfectly well on its
   * own — it is off the top of the ladder and `Math.min` says so — and sending
   * it to the bottom instead would be a second wrong answer in the same line.
   *
   * Unreachable today: the column is a Postgres integer with a 0..10 check
   * and every schema on the way in rejects a non-finite number. So this is a
   * guarantee being kept rather than a bug being fixed — and the alternative
   * is a promise in a comment that the code does not make.
   */
  const at = Number.isNaN(level) ? 0 : Math.max(0, Math.min(10, Math.round(level)))
  const hardest = hardestAt(at)

  return { size, hardest, allowed: techniquesUpTo(hardest), atLeast: floorFor(size, at) }
}

/**
 * Every skill key this game can write, for the tests that check nothing else does.
 *
 * The `sudoku.` they all start with is the game key, and it is spelt out here
 * rather than built from one. The engine is what registers `sudoku` with the
 * roster and gets it checked by the compiler; these keys are strings in a
 * database and are deliberately not derived from anything, so that renaming a
 * game cannot silently orphan a year of her levels.
 */
export const SUDOKU_SKILL_KEYS: readonly string[] = SUDOKU_SKILLS.map((skill) => skill.key)

export { SUDOKU_SIZES, TECHNIQUES }
